import { createContext, useContext, useRef } from 'react'
import { create, StoreApi, useStore } from 'zustand'

import { ToastDetails } from './toastTypings'

export interface ToastState {
  queue: ToastDetails[]
  pushToQueue: (notification: Omit<ToastDetails, 'id'>) => void
  removeFromQueue: (id: string) => void
}

export const createToastStore = () =>
  create<ToastState>((set, get) => ({
    queue: [],
    pushToQueue: (notification) => {
      const id = Date.now().toString()

      if (notification.type === 'refresh') {
        const existingToast = get().queue.find((toast) => toast.type === 'refresh')
        if (existingToast) return
      }

      const duplicatedToast = get().queue.find(
        (toast) => toast.title === notification.title && toast.description === notification.description
      )

      if (duplicatedToast && !duplicatedToast.disableDuplication) return

      set(({ queue }) => ({
        queue: [...queue, { id, ...notification }],
      }))
    },
    removeFromQueue: (id) => {
      set(({ queue }) => ({
        queue: queue.filter((notification) => notification.id !== id),
      }))
    },
  }))

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ToastStoreContext = createContext<StoreApi<ToastState>>(undefined as any)
export const ToastStoreProvider = ({ children }: { children: React.ReactNode }) => {
  const store = useRef<StoreApi<ToastState>>()

  if (!store.current) {
    store.current = createToastStore()
  }

  return <ToastStoreContext.Provider value={store.current}>{children}</ToastStoreContext.Provider>
}

export function useToastStore<T>(selector: (state: ToastState) => T) {
  const store = useContext(ToastStoreContext)

  return useStore(store, selector)
}
